/**
 * Revision-aware Insights tab
 *
 * Pure logic for:
 *  - Building human-readable revision labels from ordinal/created_at/is_active
 *  - Grouping flat insight rows by revision_id (client-side)
 *  - Computing per-revision subtotals (sum numerators; derive rates from sums)
 *  - Detecting the promoted variant from the journey config's promoted_variant_key
 *  - Building the revision_id filter for the challenger insights request
 *
 * No React in this file — pure functions, fully testable without DOM.
 */

import moment from 'moment-timezone'
import { Journey, JourneyRevision } from '../types/journey-api-response'
import type { IJourneyStep } from '@pushly/models/lib/structs/journeys'
import { StepType } from '@pushly/models/lib/enums/journeys'
import type { ReportFilter } from '../../reporting_v2/helpers/build-api-report-contract'

// ─── Types ────────────────────────────────────────────────────────────────────

/** The three report types the Journey performance (Insights) tab can render. */
export type JourneyPerformanceReport = 'NOTIFICATIONS' | 'STEPS' | 'SPLIT_TEST_VARIANTS'

/** The subset of a VARIANT_SPLIT step's configuration the revision UI reads. */
export interface VariantSplitConfiguration {
    promoted_variant_key?: string | null
    branches?: Array<{ key: string; label: string }>
}

/**
 * Nested entity breakdown objects challenger returns on an insights row, present
 * per the requested `breakdowns`. Shared across the notification / step / variant
 * reports so every breakdown row reads from one typed shape instead of `any`.
 */
export interface InsightNotificationRef {
    id?: number | string | null
    title?: string | null
    body?: string | null
    source?: string | null
    [key: string]: unknown
}

export interface InsightStepRef {
    id?: number | string | null
    name?: string | null
    type?: string | null
}

export interface InsightSplitTestRef {
    /** Graph step id of the owning VARIANT_SPLIT (= reliable per-split key). */
    step_id?: number | string | null
    name?: string | null
}

export interface InsightVariantRef {
    id?: number | string | null
    name?: string | null
    percentage?: number | null
}

/** A row returned by challenger with per-row revision identity fields. */
export interface InsightRowWithRevision {
    revision_id?: number | null
    revision_ordinal?: number | null
    revision_created_at?: string | null
    revision_is_active?: boolean | null
    // nested entity breakdown objects (present per the requested breakdowns)
    notification?: InsightNotificationRef | null
    step?: InsightStepRef | null
    split_test?: InsightSplitTestRef | null
    variant?: InsightVariantRef | null
    // metric fields (present on step/variant/notification rows)
    entrances?: number | null
    impressions?: number | null
    clicks?: number | null
    ctr_decimal?: number | null
    purchases?: number | null
    purchase_amount?: number | null
    /** Campaign-entity rate key (STEPS / SPLIT_TEST_VARIANTS). */
    purchase_rate?: number | null
    /** Notification-entity rate key. The same measure under a different name. */
    purchase_rate_decimal?: number | null
    [key: string]: unknown
}

/**
 * Metadata about one revision, derived from the journey's revisions array.
 * The `ordinal` field is 1-indexed (v1, v2, v3…) ordered by created_at ascending.
 */
export interface RevisionMeta {
    id: number
    ordinal: number
    isActive: boolean
    dateRange: string   // e.g. "Mar 8 – Apr 2"
}

/** One group in the grouped view: header metadata + the rows for that revision. */
export interface RevisionGroup<T extends InsightRowWithRevision> {
    meta: RevisionMeta
    rows: T[]
}

/** Result of client-side subtotal computation over a group's rows. */
export interface RevisionSubtotals {
    entrances: number
    impressions: number
    clicks: number
    /** Derived from summed clicks / summed impressions — never averaged from per-row ctr_decimal. */
    ctr: number | null
    purchases: number
    purchaseAmount: number
    /** Derived from summed purchases / summed clicks — never averaged. */
    purchaseRate: number | null
}

// ─── Purchase measures ────────────────────────────────────────────────────────

/**
 * The rate measure name each report's entity exposes. The notification entity calls it
 * `purchase_rate_decimal`, the campaign entity `purchase_rate` - the same measure under
 * two names. Requesting the wrong one for an entity returns nothing rather than erroring,
 * so the two must never be swapped.
 */
export const PURCHASE_RATE_FIELD_BY_REPORT: Record<JourneyPerformanceReport, string> = {
    NOTIFICATIONS: 'purchase_rate_decimal',
    STEPS: 'purchase_rate',
    SPLIT_TEST_VARIANTS: 'purchase_rate',
}

/** The purchase measures a report must request, in the order the table renders them. */
export function purchaseFieldsForReport(report: JourneyPerformanceReport): string[] {
    return ['purchases', 'purchase_amount', PURCHASE_RATE_FIELD_BY_REPORT[report]]
}

/**
 * Read the purchase rate off a row whichever entity produced it, so one table column
 * serves all three reports. Returns null when neither key is present - a missing rate is
 * unknown, not zero.
 */
export function resolvePurchaseRate(row: InsightRowWithRevision): number | null {
    return row.purchase_rate ?? row.purchase_rate_decimal ?? null
}

/**
 * The measures and dimensions each report requests from challenger.
 *
 * `campaign.id` and `revision_id` must stay in every list: the reporting backend
 * validates each filter against the requested fields, and the journey scope and revision
 * scope are both filters. `notification.source` is internal and is pruned from exports.
 *
 * Purchase measures are appended only when the domain can have purchase data, so a
 * non-e-commerce domain's request - and the CSV export built from it - is unchanged.
 */
export function buildJourneyInsightsFields(
    report: JourneyPerformanceReport,
    options: { includePurchases: boolean },
): string[] {
    const purchaseFields = options.includePurchases ? purchaseFieldsForReport(report) : []

    if (report === 'NOTIFICATIONS') {
        return [
            'campaign.id',
            'notification.id',
            'notification.title',
            'notification.body',
            'step.id',
            'step.name',
            'impressions',
            'clicks',
            'ctr_decimal',
            'notification.source',
            'revision_id',
            ...purchaseFields,
        ]
    }

    if (report === 'STEPS') {
        return [
            'campaign.id',
            'step.id',
            'step.name',
            'step.type',
            'entrances',
            'impressions',
            'clicks',
            'ctr_decimal',
            'revision_id',
            ...purchaseFields,
        ]
    }

    return [
        'campaign.id',
        'split_test.name',
        'variant.id',
        'variant.name',
        'variant.percentage',
        'entrances',
        'impressions',
        'clicks',
        'ctr_decimal',
        'revision_id',
        ...purchaseFields,
    ]
}

// ─── Revision Metadata Helpers ────────────────────────────────────────────────

/**
 * Format an ISO date string as "MMM D" (e.g. "Mar 8") in the domain's time zone, so
 * revision windows read in the same zone the rest of the domain's reporting uses.
 * Falls back to UTC when no zone is supplied.
 */
function shortDateLabel(isoStr: string | Date, timeZone?: string): string {
    return moment.tz(isoStr, timeZone || 'UTC').format('MMM D')
}

/**
 * Build sorted, 1-indexed RevisionMeta entries from a journey's revisions[].
 * Sorted ascending by created_at (oldest first → lowest ordinal).
 * Active revision gets the "Active" tag in its label.
 */
export function buildRevisionMetaList(
    journey: Pick<Journey, 'revisions' | 'date_end' | 'date_completed_utc'>,
    timeZone?: string,
): RevisionMeta[] {
    const revisions = journey.revisions ?? []
    if (revisions.length === 0) return []

    // Sort ascending by created_at, with the active revision always LAST (highest ordinal).
    // The active revision is always the current/newest (post-promotion) one, so it must receive
    // the highest ordinal number regardless of created_at — the API may assign it an earlier
    // timestamp than the superseded split revision in some promotion flows.
    const sorted = [...revisions].sort((a, b) => {
        // Active revision always sorts last (highest ordinal = newest)
        if (a.is_active && !b.is_active) return 1
        if (!a.is_active && b.is_active) return -1
        // Among non-active revisions, sort by created_at ascending (oldest first)
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    })

    const journeyEndDate = journey.date_completed_utc ?? journey.date_end

    return sorted.map((rev, idx) => {
        const ordinal = idx + 1
        const nextRev = sorted[idx + 1]
        const startLabel = shortDateLabel(rev.created_at, timeZone)

        // The end of this revision's window is normally the next revision's start.
        // BUT the sort above forces the active revision LAST regardless of created_at
        // (the API can stamp the post-promotion active revision EARLIER than the
        // superseded split). In that case nextRev.created_at is older than this rev's
        // start, which would render a backwards range ("Apr 10 – Apr 2"). Only use the
        // next revision's start when it's actually later; otherwise fall back to the
        // journey end / "Today".
        const revStart = new Date(rev.created_at).getTime()
        const nextStart = nextRev ? new Date(nextRev.created_at).getTime() : null
        let endLabel: string
        if (nextStart != null && nextStart >= revStart) {
            endLabel = shortDateLabel(nextRev!.created_at, timeZone)
        } else if (journeyEndDate) {
            endLabel = shortDateLabel(journeyEndDate, timeZone)
        } else {
            endLabel = 'Today'
        }

        const dateRange = `${startLabel} – ${endLabel}`

        return {
            id: rev.id,
            ordinal,
            isActive: rev.is_active,
            dateRange,
        }
    })
}

/**
 * The single source of truth for a revision's human-facing label, shared by every
 * revision control (Workflow selector, Insights scope select, past-revision context
 * strip) so all surfaces read identically. Revisions can't be named in the UI yet,
 * so we use the ordinal form: the active revision is "Current revision", past
 * revisions are "Revision N". The "Active" badge is rendered separately (so we don't
 * append "· Active" text here).
 */
export function revisionDisplayLabel(meta: RevisionMeta): string {
    return meta.isActive ? 'Current revision' : `Revision ${meta.ordinal}`
}

// ─── Grouping ─────────────────────────────────────────────────────────────────

/**
 * Group flat insight rows by their revision_id.
 * Rows without a revision_id are placed into the active revision's group
 * (fall-back: last group in sorted order, i.e. active).
 * Returns groups in descending ordinal order (newest first) to match UX spec.
 */
export function groupRowsByRevision<T extends InsightRowWithRevision>(
    rows: T[],
    metaList: RevisionMeta[],
): RevisionGroup<T>[] {
    if (metaList.length === 0) return []

    const groupMap = new Map<number, T[]>()

    // Initialize all revision buckets in sorted order
    const activeId = metaList.find((m) => m.isActive)?.id ?? metaList[metaList.length - 1].id
    for (const meta of metaList) {
        groupMap.set(meta.id, [])
    }

    for (const row of rows) {
        const rid = row.revision_id ?? activeId
        const bucket = groupMap.get(rid)
        if (bucket) {
            bucket.push(row)
        } else {
            // revision_id from API not found in journey.revisions — put in active
            const fallback = groupMap.get(activeId)!
            fallback.push(row)
        }
    }

    // Return in descending ordinal order (newest first)
    return [...metaList]
        .reverse()
        .map((meta) => ({
            meta,
            rows: groupMap.get(meta.id) ?? [],
        }))
}

// ─── Subtotals ────────────────────────────────────────────────────────────────

/**
 * Compute per-revision subtotals from the raw API rows.
 * Rates are DERIVED from summed counts — never averaged from per-row rate fields.
 *   ctr  = sum(clicks) / sum(impressions)
 *   purchaseRate = sum(purchases) / sum(clicks)
 */
export function computeRevisionSubtotals(rows: InsightRowWithRevision[]): RevisionSubtotals {
    let entrances = 0
    let impressions = 0
    let clicks = 0
    let purchases = 0
    let purchaseAmount = 0

    for (const row of rows) {
        entrances += row.entrances ?? 0
        impressions += row.impressions ?? 0
        clicks += row.clicks ?? 0
        purchases += row.purchases ?? 0
        purchaseAmount += row.purchase_amount ?? 0
    }

    const ctr = impressions > 0 ? clicks / impressions : null
    const purchaseRate = clicks > 0 ? purchases / clicks : null

    return { entrances, impressions, clicks, ctr, purchases, purchaseAmount, purchaseRate }
}

// ─── Promoted Variant Detection ───────────────────────────────────────────────

/**
 * Scan all revisions (not just the active one) for the first VARIANT_SPLIT step
 * whose configuration carries a non-empty promoted_variant_key. Returns the owning
 * revision plus the promoted branch key, or null if no revision has a promoted split.
 */
function findPromotedSplit(
    journey: Pick<Journey, 'revisions'>,
): { revision: JourneyRevision; key: string } | null {
    const revisions = journey.revisions ?? []
    for (const rev of revisions) {
        const steps: IJourneyStep[] = rev.steps ?? []
        for (const step of steps) {
            if (step.type === StepType.VARIANT_SPLIT) {
                const cfg = step.configuration as VariantSplitConfiguration | undefined
                const key = cfg?.promoted_variant_key
                if (key != null && key !== '') {
                    return { revision: rev, key: String(key) }
                }
            }
        }
    }
    return null
}

/**
 * Find the promoted variant's branch key from the journey's revisions' step
 * configurations, or null if none is promoted. The UI uses this to stamp 🏆 on the
 * matching variant row.
 */
export function findPromotedVariantKey(journey: Pick<Journey, 'revisions'>): string | null {
    return findPromotedSplit(journey)?.key ?? null
}

/**
 * Given a variant API row, check whether it is the promoted variant.
 *
 * `variant.id` is the branch KEY (a stable nanoid) — the same value challenger
 * stores in `promoted_variant_key` — so we match on it directly.
 */
export function isPromotedVariantRow(
    row: { variant?: { id?: number | string; name?: string } },
    promotedVariantKey: string | null,
): boolean {
    if (!promotedVariantKey) return false
    const id = row.variant?.id
    return id != null && String(id) === promotedVariantKey
}

// ─── Scope defaults ───────────────────────────────────────────────────────────

export type RevisionScope = 'all' | number  // number = specific revision_id

/**
 * Compute the default scope for a given report type.
 *   Notifications & Steps → 'all'
 *   Split Test Variants   → the revision_id of the revision that contained the split
 *                           (the one with a VARIANT_SPLIT step having data),
 *                           falling back to 'all' if not deterministic.
 */
export function defaultScopeForReport(
    report: JourneyPerformanceReport,
    journey: Pick<Journey, 'revisions'>,
): RevisionScope {
    if (report !== 'SPLIT_TEST_VARIANTS') return 'all'

    // Prefer the revision whose VARIANT_SPLIT step has a promoted_variant_key set.
    const promoted = findPromotedSplit(journey)
    if (promoted) return promoted.revision.id

    // Fallback: any revision with a VARIANT_SPLIT step
    const revisions = journey.revisions ?? []
    for (const rev of revisions) {
        for (const step of rev.steps ?? []) {
            if (step.type === StepType.VARIANT_SPLIT) {
                return rev.id
            }
        }
    }
    return 'all'
}

// ─── Challenger request helpers ───────────────────────────────────────────────

/**
 * Build the revision_id filter object to append to a challenger insights request.
 * Uses the shared `ReportFilter` shape on the generic `filters` channel:
 *   { field: 'revision_id', operator: 'in', value: [id] }.
 *
 * This is the chosen contract channel (vs. top-level `revisionId`) because it matches
 * the canonical Org Insights request filter pattern (buildInsightsPackageForView).
 * challenger lowercases every operator on ingest (filters.request-transformer) and
 * resolveRevisionIdFilter() matches on `field` alone — reading
 *   filters.find(f => f.field === 'revision_id').value[0]
 * — so the operator's exact value is immaterial; we emit the canonical lowercase 'in'.
 */
export function buildRevisionIdFilter(revisionId: number): ReportFilter {
    return {
        field: 'revision_id',
        operator: 'in',
        value: [revisionId],
    }
}

// ─── Single-revision guard ────────────────────────────────────────────────────

/**
 * Returns true when the journey has only one revision — in this case,
 * the Insights tab shows zero new chrome (no revision selector, no grouping).
 */
export function isSingleRevisionJourney(journey: Pick<Journey, 'revisions'>): boolean {
    return (journey.revisions?.length ?? 0) <= 1
}
